import React, { useState, useEffect, useMemo, useRef } from 'react';
import { formatPositionsDisplay } from '../../services/positionAPIs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button
} from '@mui/material';
import { useReactToPrint } from 'react-to-print';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { getEmployeeAttendanceReport } from '../../services/reportingAPIs';
import { IMAGE_BASE_URL } from '../../config';

const COLORS = ['#2196f3', '#f44336', '#ff9800', '#4caf50', '#9c27b0', '#607d8b'];

// Helper: Calculate expected working hours between two dates (inclusive)
const calculateExpectedHours = (startDate, endDate) => {
  if (startDate > endDate) return 0;
  const diffTime = endDate - startDate;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // inclusive
  return (diffDays / 7) * 44; // 44 hours per week
};

// Helper: Get start and end of a calendar month
const getMonthRange = (year, month) => {
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0); // last day of month
  return { start, end };
};

const EmployeeAttendanceReportDialog = ({ open, onClose, employeeId, endDate }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [error, setError] = useState(null);

  const printRef = useRef();

  // const handlePrint = useReactToPrint({
  //   contentRef: printRef, 
  //   documentTitle: `Attendance_Report_${employeeId}`,
  //   // Optional: customize page style for print
  //   pageStyle: `
  //     @media print {
  //       body {
  //         -webkit-print-color-adjust: exact;
  //         print-color-adjust: exact;
  //       }
  //       .print-full-width {
  //         width: 100% !important;
  //         max-width: 100% !important;
  //         flex-basis: 100% !important;
  //         page-break-inside: avoid;
  //       }
  //       .MuiCard-root {
  //         break-inside: avoid;
  //         page-break-inside: avoid;
  //       }
  //     }
  //   `
  // });
  const handlePrint = useReactToPrint({
  contentRef: printRef,
  documentTitle: `Attendance_Report_${employeeId}`,
  pageStyle: `
    @media print {
      @page {
         size: A4; 
        margin: 0.1cm;
      }
      body {
        margin: 0;
        padding: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .print-report-container {
        transform: scale(0.75); /* shrink everything */
        transform-origin: top Left;
         width: 140%;                    /* expand logical width so scaling fits */
    height: auto;
    page-break-inside: avoid;    
      }
      .recharts-responsive-container {
        width: 45% !important;
        height: 230px !important; /* keep charts fully visible */
      }
      .no-print, .MuiDialogTitle-root, .MuiDialogActions-root, button {
        display: none !important;
      }
        .print-full-width {
           width: 45% !important;
           max-width: 50% !important;
        }
         .MuiCard-root {
      }
        
    }
  `
});


  useEffect(() => {
    if (open && employeeId) {
      fetchData();
    }
  }, [open, employeeId, endDate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getEmployeeAttendanceReport(employeeId, endDate);
      setData(result);
      console.log('employee Report:', result);
      
      if (result.monthlyAttendance.length > 0) {
        const years = [...new Set(result.monthlyAttendance.map(m => m.year))];
        setSelectedYear(Math.max(...years));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Enrich monthly data with expectedHours and estimated worked hours
  const enrichedMonthlyData = useMemo(() => {
    if (!data) return [];

    const joiningDate = new Date(data.employee.dateOfJoining);
    const reportEnd = new Date(data.reportPeriodEnd);

    const monthNameToNumber = {
      JANUARY: 1,
      FEBRUARY: 2,
      MARCH: 3,
      APRIL: 4,
      MAY: 5,
      JUNE: 6,
      JULY: 7,
      AUGUST: 8,
      SEPTEMBER: 9,
      OCTOBER: 10,
      NOVEMBER: 11,
      DECEMBER: 12
    };

    return data.monthlyAttendance.map(m => {
      const monthNum = monthNameToNumber[m.month] || 1;
      const { start: monthStart, end: monthEnd } = getMonthRange(m.year, monthNum);

      // Effective period: within month, after joining, before report end
      const effectiveStart = joiningDate > monthStart ? joiningDate : monthStart;
      const effectiveEnd = reportEnd < monthEnd ? reportEnd : monthEnd;

      const expectedHours = calculateExpectedHours(effectiveStart, effectiveEnd);

      // Total absence hours (all types that reduce worked time)
      const absenceHours = 
        (m.sicknessHours || 0) +
        (m.annualLeaveHours || 0) +
        (m.personalHours || 0) +
        (m.unpaidLeaveHours || 0);

      const estimatedWorkedHours = Math.max(0, expectedHours - absenceHours);

      return {
        ...m,
        expectedHours: Math.max(0, expectedHours),
        totalHoursWorked: estimatedWorkedHours,
      };
    });
  }, [data]);

  const availableYears = useMemo(() => {
    if (!enrichedMonthlyData.length) return [];
    return [...new Set(enrichedMonthlyData.map(m => m.year))].sort((a, b) => b - a);
  }, [enrichedMonthlyData]);

  const filteredData = useMemo(() => {
    if (!selectedYear) return enrichedMonthlyData;
    return enrichedMonthlyData.filter(m => m.year === selectedYear);
  }, [enrichedMonthlyData, selectedYear]);

  const yearlyTotals = useMemo(() => {
    if (enrichedMonthlyData.length === 0) return {};
    
    const totals = {};
    availableYears.forEach(year => {
      const yearData = enrichedMonthlyData.filter(m => m.year === year);
      totals[year] = {
        sicknessRecords: yearData.reduce((sum, m) => sum + (m.sicknessRecords || 0), 0),
        latenessRecords: yearData.reduce((sum, m) => sum + (m.latenessRecords || 0), 0),
        annualLeaveDays: yearData.reduce((sum, m) => sum + (m.annualLeaveDays || 0), 0),
        unpaidLeaveDays: yearData.reduce((sum, m) => sum + (m.unpaidLeaveDays || 0), 0),
        personalRecords: yearData.reduce((sum, m) => sum + (m.personalRecords || 0), 0),
        totalHoursWorked: yearData.reduce((sum, m) => sum + (m.totalHoursWorked || 0), 0),
        expectedHours: yearData.reduce((sum, m) => sum + (m.expectedHours || 0), 0)
      };
    });
    return totals;
  }, [enrichedMonthlyData, availableYears]);

  const overallTotals = useMemo(() => {
    return {
      sicknessRecords: enrichedMonthlyData.reduce((sum, m) => sum + (m.sicknessRecords || 0), 0),
      latenessRecords: enrichedMonthlyData.reduce((sum, m) => sum + (m.latenessRecords || 0), 0),
      annualLeaveDays: enrichedMonthlyData.reduce((sum, m) => sum + (m.annualLeaveDays || 0), 0),
      unpaidLeaveDays: enrichedMonthlyData.reduce((sum, m) => sum + (m.unpaidLeaveDays || 0), 0),
      personalRecords: enrichedMonthlyData.reduce((sum, m) => sum + (m.personalRecords || 0), 0),
      totalHoursWorked: enrichedMonthlyData.reduce((sum, m) => sum + (m.totalHoursWorked || 0), 0),
      expectedHours: enrichedMonthlyData.reduce((sum, m) => sum + (m.expectedHours || 0), 0)
    };
  }, [enrichedMonthlyData]);

  const chartData = useMemo(() => {
    return filteredData.map(month => ({
      month: month.monthLabel,
      sickness: month.sicknessRecords || 0,
      lateness: month.latenessRecords || 0,
      leave: month.annualLeaveDays || 0,
      unpaidLeave: month.unpaidLeaveDays || 0,
      personal: month.personalRecords || 0,
      hoursWorked: Math.round(month.totalHoursWorked || 0),
      expectedHours: Math.round(month.expectedHours || 0)
    }));
  }, [filteredData]);

  const summaryData = useMemo(() => {
    const totals = selectedYear ? yearlyTotals[selectedYear] : overallTotals;
    return [
      { name: 'Sickness', value: totals.sicknessRecords || 0 },
      { name: 'Lateness', value: totals.latenessRecords || 0 },
      { name: 'Vacation', value: totals.annualLeaveDays || 0 },
      { name: 'Unpaid Leave', value: totals.unpaidLeaveDays || 0 },
      { name: 'P.C', value: totals.personalRecords || 0 }
    ].filter(item => item.value > 0);
  }, [selectedYear, yearlyTotals, overallTotals]);

  const handleYearChange = (event, newYear) => {
    if (newYear !== null) {
      setSelectedYear(newYear);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
        <Button variant="outlined" size="small" onClick={handlePrint}>
          Download as PDF
        </Button>
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        
        {data && !loading && (
          <Box ref={printRef} className="print-report-container" sx={{ p: 2 }}>
            {/* Employee Info */}
            <Card sx={{ mb: 3, width: '50%', margin: 'auto', mt: 0, display: 'flex', justifyContent: 'center' }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Avatar
                        src={data.employee.image ? `${IMAGE_BASE_URL}${data.employee.image}` : undefined}
                        sx={{ width: 70, height: 70, mb: 2, ml:'auto', mr:'auto' }}
                      >
                        {data.employee.firstName[0]}{data.employee.lastName[0]}
                      </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap:3 }}>
                      
                      <Typography variant="h5" sx={{ textAlign: 'center' }}>
                        {data.employee.firstName} {data.employee.lastName}
                      </Typography>
                      <Typography variant="h5" color="text.secondary">
                        {formatPositionsDisplay(data.employee)}
                      </Typography>
                      <Typography variant="h5" color="text.secondary">
                        {data.employee.department?.name} • {data.employee.office?.name}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Overall Totals */}
            <Card sx={{ mb: 3, width: '90%', margin: 'auto', mt: 0, display: 'flex', justifyContent: 'center' }}>
              <CardContent>
                <Typography variant="subtitle2" color="green" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                  Overall Totals ({data.reportPeriodStart} to {data.reportPeriodEnd})
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                      <Typography variant="h6" color="primary">{overallTotals.sicknessRecords}</Typography>
                      <Typography variant="caption">Sick Days</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'var(--warning-light, #fff3e0)', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: '#f57c00' }}>{overallTotals.latenessRecords}</Typography>
                      <Typography variant="caption">Late Records</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e0f7fa', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: '#006064' }}>{overallTotals.personalRecords}</Typography>
                      <Typography variant="caption">Personal Commitment</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f9d6e3ff', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: '#640026ff' }}>{overallTotals.unpaidLeaveDays}</Typography>
                      <Typography variant="caption">Unpaid Leave Days</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f3e5f5', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: '#7b1fa2' }}>{overallTotals.annualLeaveDays}</Typography>
                      <Typography variant="caption">Leave Days</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: '#388e3c' }}>
                        {Math.round(overallTotals.workedHours || overallTotals.totalHoursWorked)}h
                      </Typography>
                      <Typography variant="caption">Worked Hours</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f1f8e9', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: '#558b2f' }}>
                        {Math.round(overallTotals.expectedHours)}h
                      </Typography>
                      <Typography variant="caption">Expected Hours</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Year Selector */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                value={selectedYear}
                exclusive
                onChange={handleYearChange}
                size="small"
              >
                {availableYears.map(year => (
                  <ToggleButton key={year} value={year} sx={{ minWidth: 100 }}>
                    {year}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Year Totals */}
            {selectedYear && (
              <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                    {selectedYear} Totals
                  </Typography>
                  <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={6} sm={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6">{yearlyTotals[selectedYear]?.sicknessRecords || 0}</Typography>
                        <Typography variant="caption">Sick Days</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6">{yearlyTotals[selectedYear]?.latenessRecords || 0}</Typography>
                        <Typography variant="caption">Late Records</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6">{yearlyTotals[selectedYear]?.personalRecords || 0}</Typography>
                        <Typography variant="caption">Personal</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6">{yearlyTotals[selectedYear]?.unpaidLeaveDays || 0}</Typography>
                        <Typography variant="caption">Unpaid Leave</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6">{yearlyTotals[selectedYear]?.annualLeaveDays || 0}</Typography>
                        <Typography variant="caption">Leave Days</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6">{Math.round(yearlyTotals[selectedYear]?.totalHoursWorked || 0)}h</Typography>
                        <Typography variant="caption">Worked Hours</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6">{Math.round(yearlyTotals[selectedYear]?.expectedHours || 0)}h</Typography>
                        <Typography variant="caption">Expected Hours</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Charts */}
            <Grid className="print-charts-container" container spacing={2}>
              <Grid item sx={{width:'30%'}} xs={12} md={8}  className="print-full-width" >
                <Card className="print-chart-row">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Hours Worked vs Expected</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="hoursWorked" fill="#21bbf3ff" name="Worked Hours" />
                        <Bar dataKey="expectedHours" fill="#27a738ff" name="Expected Hours" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item sx={{width:'35%'}} xs={12} md={8}  className="print-full-width" >
                <Card className="print-chart-row">
                  <CardContent sx={{padding:0, mt:3}}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Attendance Summary</Typography>
                    {summaryData && summaryData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart style={{marginLeft:15}}>
                        <Pie
                          data={summaryData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {summaryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 300,
                        color: 'text.secondary',
                        textAlign: 'center',
                        px: 2,
                     }}
                    > 
                      <CheckCircleIcon sx={{ fontSize: 60, mb: 2, color: 'success.main' }} />
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        Perfect Attendance!
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        No leaves, vacations, or absences recorded.
                      </Typography>
                    </Box>
                 )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item sx={{width:'30%'}} xs={12} md={8}  className="print-full-width" >
                <Card className="print-chart-row">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Monthly Attendance Records</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="sickness" stroke="#2196f3" name="Sickness" />
                        <Line type="monotone" dataKey="lateness" stroke="#f44336" name="Lateness" />
                        <Line type="monotone" dataKey="leave" stroke="#9c27b0" name="Annual Leave" />
                         <Line type="monotone" dataKey="unpaidLeave" stroke="#3eb027ff" name="Unpaid Leave" />
                        <Line type="monotone" dataKey="personal" stroke="#607d8b" name="Personal Commitment" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeAttendanceReportDialog;

// import React, { useState, useEffect, useMemo } from 'react';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   IconButton,
//   Avatar,
//   CircularProgress,
//   ToggleButtonGroup,
//   ToggleButton,
//   Card,
//   CardContent,
//   Typography,
//   Box,
//   Grid
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import {
//   LineChart,
//   Line,
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
//   PieChart,
//   Pie,
//   Cell
// } from 'recharts';
// import { getEmployeeAttendanceReport } from '../../services/reportingAPIs';
// import { IMAGE_BASE_URL } from '../../config';

// const COLORS = ['#2196f3', '#f44336', '#ff9800', '#4caf50', '#9c27b0'];

// const EmployeeAttendanceReportDialog = ({ open, onClose, employeeId, endDate }) => {
//   const [loading, setLoading] = useState(false);
//   const [data, setData] = useState(null);
//   const [selectedYear, setSelectedYear] = useState(null);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     if (open && employeeId) {
//       fetchData();
//     }
//   }, [open, employeeId, endDate]);

//   const fetchData = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const result = await getEmployeeAttendanceReport(employeeId, endDate);
//       setData(result);
      
//       // Set default year to the most recent
//       if (result.monthlyAttendance.length > 0) {
//         const years = [...new Set(result.monthlyAttendance.map(m => m.year))];
//         setSelectedYear(Math.max(...years));
//       }
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const availableYears = useMemo(() => {
//     if (!data) return [];
//     return [...new Set(data.monthlyAttendance.map(m => m.year))].sort((a, b) => b - a);
//   }, [data]);

//   const filteredData = useMemo(() => {
//     if (!data || !selectedYear) return [];
//     return data.monthlyAttendance.filter(m => m.year === selectedYear);
//   }, [data, selectedYear]);

//   const yearlyTotals = useMemo(() => {
//     if (!data) return {};
    
//     const totals = {};
//     availableYears.forEach(year => {
//       const yearData = data.monthlyAttendance.filter(m => m.year === year);
//       totals[year] = {
//         sicknessRecords: yearData.reduce((sum, m) => sum + (m.sicknessRecords || 0), 0),
//         latenessRecords: yearData.reduce((sum, m) => sum + (m.latenessRecords || 0), 0),
//         annualLeaveDays: yearData.reduce((sum, m) => sum + (m.annualLeaveDays || 0), 0),
//         totalHoursWorked: yearData.reduce((sum, m) => sum + (m.totalHoursWorked || 0), 0),
//         expectedHours: yearData.reduce((sum, m) => sum + (m.expectedHours || 0), 0)
//       };
//     });
    
//     return totals;
//   }, [data, availableYears]);

//   const overallTotals = useMemo(() => {
//     if (!data) return {};
    
//     return {
//       sicknessRecords: data.monthlyAttendance.reduce((sum, m) => sum + (m.sicknessRecords || 0), 0),
//       latenessRecords: data.monthlyAttendance.reduce((sum, m) => sum + (m.latenessRecords || 0), 0),
//       annualLeaveDays: data.monthlyAttendance.reduce((sum, m) => sum + (m.annualLeaveDays || 0), 0),
//       totalHoursWorked: data.monthlyAttendance.reduce((sum, m) => sum + (m.totalHoursWorked || 0), 0),
//       expectedHours: data.monthlyAttendance.reduce((sum, m) => sum + (m.expectedHours || 0), 0)
//     };
//   }, [data]);

//   const chartData = useMemo(() => {
//     return filteredData.map(month => ({
//       month: month.monthLabel,
//       sickness: month.sicknessRecords || 0,
//       lateness: month.latenessRecords || 0,
//       leave: month.annualLeaveDays || 0,
//       hoursWorked: Math.round(month.totalHoursWorked || 0),
//       expectedHours: Math.round(month.expectedHours || 0)
//     }));
//   }, [filteredData]);

//   const summaryData = useMemo(() => {
//     const totals = selectedYear ? yearlyTotals[selectedYear] : overallTotals;
//     return [
//       { name: 'Sickness', value: totals.sicknessRecords || 0 },
//       { name: 'Lateness', value: totals.latenessRecords || 0 },
//       { name: 'Annual Leave', value: totals.annualLeaveDays || 0 }
//     ].filter(item => item.value > 0);
//   }, [selectedYear, yearlyTotals, overallTotals]);

//   const handleYearChange = (event, newYear) => {
//     if (newYear !== null) {
//       setSelectedYear(newYear);
//     }
//   };

//   if (!open) return null;

//   return (
//     <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
//       <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
//         {/* <Typography variant="h6">Employee Attendance Report</Typography> */}
//         <IconButton onClick={onClose} size="small">
//           <CloseIcon />
//         </IconButton>
//       </DialogTitle>
      
//       <DialogContent>
//         {loading && (
//           <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
//             <CircularProgress />
//           </Box>
//         )}
        
//         {error && (
//           <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, mb: 2 }}>
//             <Typography color="error">{error}</Typography>
//           </Box>
//         )}
        
//         {data && !loading && (
//           <>
//             {/* Employee Info Section */}
//             <Card sx={{ mb: 3 , width: '50%', margin:'auto', mt:0 , display:'flex', justifyContent:'center' }}>
//               <CardContent>
//                 <Grid container spacing={3}>
//                   <Grid item xs={12} md={3}>
//                     <Avatar
//                         src={data.employee.image ? `${IMAGE_BASE_URL}${data.employee.image}` : undefined}
//                         sx={{ width: 70, height: 70, mb: 2, ml:'auto', mr:'auto' }}
//                       >
//                         {data.employee.firstName[0]}{data.employee.lastName[0]}
//                       </Avatar>
//                     <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap:3 }}>
                      
//                       <Typography variant="h4" sx={{ textAlign: 'center' }}>
//                         {data.employee.firstName} {data.employee.lastName}
//                       </Typography>
//                       <Typography variant="h4" color="text.secondary">
//                         {data.employee.position}
//                       </Typography>
//                       <Typography variant="h4" color="text.secondary">
//                         {data.employee.department?.name} • {data.employee.office?.name}
//                       </Typography>
//                     </Box>
//                   </Grid>
//                 </Grid>
//               </CardContent>
//             </Card>
//             <Card sx={{ mb: 3 , width: '50%',  margin:'auto',  mt:0, display:'flex', justifyContent:'center'  }}>
//               <CardContent>
//                   <Grid item xs={12} md={9}>
//                     <Typography variant="subtitle2" color='green' sx={{width:'100%', mb: 2, fontWeight: 'bold', ml:'auto'}}>
//                       Overall Totals ({data.reportPeriodStart} to {data.reportPeriodEnd})
//                     </Typography>
//                     <Grid container spacing={2}>
//                       <Grid item xs={6} sm={3}>
//                         <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
//                           <Typography variant="h5" color="primary">{overallTotals.sicknessRecords}</Typography>
//                           <Typography variant="caption">Sick Days</Typography>
//                         </Box>
//                       </Grid>
//                       <Grid item xs={6} sm={3}>
//                         <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'var(--warning-light, #fff3e0)', borderRadius: 1 }}>
//                           <Typography variant="h5" sx={{ color: '#f57c00' }}>{overallTotals.latenessRecords}</Typography>
//                           <Typography variant="caption">Late Records</Typography>
//                         </Box>
//                       </Grid>
//                       <Grid item xs={6} sm={3}>
//                         <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f3e5f5', borderRadius: 1 }}>
//                           <Typography variant="h5" sx={{ color: '#7b1fa2' }}>{overallTotals.annualLeaveDays}</Typography>
//                           <Typography variant="caption">Leave Days</Typography>
//                         </Box>
//                       </Grid>
//                       <Grid item xs={6} sm={3}>
//                         <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
//                           <Typography variant="h5" sx={{ color: '#388e3c' }}>
//                             {Math.round(overallTotals.totalHoursWorked)}h
//                           </Typography>
//                           <Typography variant="caption">Hours Worked</Typography>
//                         </Box>
//                       </Grid>
//                     </Grid>
//                   </Grid>
//               </CardContent>
//             </Card>

//             {/* Year Selector */}
//             <Box sx={{ mb: 3, display: 'flex', width: '100%' }}>
//               <ToggleButtonGroup
//                 value={selectedYear}
//                 exclusive
//                 onChange={handleYearChange}
//                 size="small"
//                 sx={{width:'100%'}}
//               >
//                 {availableYears.map(year => (
//                   <ToggleButton key={year} value={year} sx={{minWidth:150}}>
//                     {year}
//                   </ToggleButton>
//                 ))}
//               </ToggleButtonGroup>
//             </Box>

//             {/* Year Totals */}
//             {selectedYear && (
//               <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
//                 <CardContent>
//                   <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
//                     {selectedYear} Totals
//                   </Typography>
//                   <Grid container spacing={2}>
//                     <Grid item xs={6} sm={2.4}>
//                       <Box sx={{ textAlign: 'center' }}>
//                         <Typography variant="h6">{yearlyTotals[selectedYear]?.sicknessRecords || 0}</Typography>
//                         <Typography variant="caption">Sick Days</Typography>
//                       </Box>
//                     </Grid>
//                     <Grid item xs={6} sm={2.4}>
//                       <Box sx={{ textAlign: 'center' }}>
//                         <Typography variant="h6">{yearlyTotals[selectedYear]?.latenessRecords || 0}</Typography>
//                         <Typography variant="caption">Late Records</Typography>
//                       </Box>
//                     </Grid>
//                     <Grid item xs={6} sm={2.4}>
//                       <Box sx={{ textAlign: 'center' }}>
//                         <Typography variant="h6">{yearlyTotals[selectedYear]?.annualLeaveDays || 0}</Typography>
//                         <Typography variant="caption">Leave Days</Typography>
//                       </Box>
//                     </Grid>
//                     <Grid item xs={6} sm={2.4}>
//                       <Box sx={{ textAlign: 'center' }}>
//                         <Typography variant="h6">{Math.round(yearlyTotals[selectedYear]?.totalHoursWorked || 0)}h</Typography>
//                         <Typography variant="caption">Hours Worked</Typography>
//                       </Box>
//                     </Grid>
//                     <Grid item xs={6} sm={2.4}>
//                       <Box sx={{ textAlign: 'center' }}>
//                         <Typography variant="h6">{Math.round(yearlyTotals[selectedYear]?.expectedHours || 0)}h</Typography>
//                         <Typography variant="caption">Expected Hours</Typography>
//                       </Box>
//                     </Grid>
//                   </Grid>
//                 </CardContent>
//               </Card>
//             )}

//             {/* Charts */}
//             <Grid container spacing={3}>
//               <Grid item sx={{width:'30%'}} xs={12} md={8}>
//                 <Card>
//                   <CardContent>
//                     <Typography variant="h6" sx={{ mb: 2 }}>Hours Worked vs Expected</Typography>
//                     <ResponsiveContainer width="100%" height={300}>
//                       <BarChart data={chartData}>
//                         <CartesianGrid strokeDasharray="3 3" />
//                         <XAxis dataKey="month" />
//                         <YAxis />
//                         <Tooltip />
//                         <Legend />
//                         <Bar dataKey="hoursWorked" fill="#4caf50" name="Hours Worked" />
//                         <Bar dataKey="expectedHours" fill="#e0e0e0" name="Expected Hours" />
//                       </BarChart>
//                     </ResponsiveContainer>
//                   </CardContent>
//                 </Card>
//               </Grid>

//               <Grid item sx={{width:'33%'}} xs={12} md={4}>
//                 <Card>
//                   <CardContent sx={{padding:0}} >
//                     <Typography variant="h6" sx={{ mb: 2 }}>Attendance Summary</Typography>
//                     <ResponsiveContainer width="100%" height={300}>
//                       <PieChart>
//                         <Pie
//                           data={summaryData}
//                           cx="50%"
//                           cy="50%"
//                           labelLine={false}
//                           label={({ name, value }) => `${name}: ${value}`}
//                           outerRadius={80}
//                           fill="#8884d8"
//                           dataKey="value"
//                         >
//                           {summaryData.map((entry, index) => (
//                             <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
//                           ))}
//                         </Pie>
//                         <Tooltip />
//                       </PieChart>
//                     </ResponsiveContainer>
//                   </CardContent>
//                 </Card>
//               </Grid>

//               <Grid  sx={{width:'30%'}} item xs={12}>
//                 <Card>
//                   <CardContent>
//                     <Typography variant="h6" sx={{ mb: 2 }}>Monthly Attendance Records</Typography>
//                     <ResponsiveContainer width="100%" height={300}>
//                       <LineChart data={chartData}>
//                         <CartesianGrid strokeDasharray="3 3" />
//                         <XAxis dataKey="month" />
//                         <YAxis />
//                         <Tooltip />
//                         <Legend />
//                         <Line type="monotone" dataKey="sickness" stroke="#2196f3" name="Sickness" />
//                         <Line type="monotone" dataKey="lateness" stroke="#f44336" name="Lateness" />
//                         <Line type="monotone" dataKey="leave" stroke="#9c27b0" name="Annual Leave" />
//                       </LineChart>
//                     </ResponsiveContainer>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             </Grid>
//           </>
//         )}
//       </DialogContent>
//     </Dialog>
//   );
// };

// export default EmployeeAttendanceReportDialog;